// utils/useDefaultInfiniteQuery.ts
import {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  useInfiniteQuery,
} from "@tanstack/react-query"
import { useHandleAsyncSafely } from "../typed-client/hooks/use-handle-async-safely"

import { Pagination } from "./Pagination"
import { getDefaultQueryKey } from "./getDefaultQueryKey"
import { PageRequest } from "@/orval/gen"

interface IUseDefaultInfiniteQueryOptions<
  TData extends Pagination<unknown>,
  TError = Error,
> extends Partial<
  UseInfiniteQueryOptions<
    TData,
    TError,
    InfiniteData<TData>,
    QueryKey,
    PageRequest
  >
> {
  pageSize?: number
}

export function useDefaultInfiniteQuery<
  // args and and return of the queryFn can be anything
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TQueryFn extends (...args: any[]) => Promise<TData>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends Pagination<any> = Awaited<ReturnType<TQueryFn>>,
>(
  queryFn: TQueryFn,
  paramsOrOptions?:
    | ((pageRequest: {
        pageParam: number
        pageSize: number
      }) => Parameters<TQueryFn>[0])
    | IUseDefaultInfiniteQueryOptions<TData>,
  maybeOptions?: IUseDefaultInfiniteQueryOptions<TData>
) {
  const handleAsyncSafely = useHandleAsyncSafely<TData>()

  const isGetParams = typeof paramsOrOptions === "function"

  const getParams: Parameters<TQueryFn>[0] = isGetParams
    ? paramsOrOptions
    : () => {}
  const options: IUseDefaultInfiniteQueryOptions<TData> | undefined =
    isGetParams ? (maybeOptions ?? undefined) : (paramsOrOptions ?? undefined)

  return useInfiniteQuery<
    TData,
    Error,
    InfiniteData<TData>,
    QueryKey,
    PageRequest
  >({
    queryKey: getDefaultQueryKey(queryFn, getParams()),
    queryFn: ({ pageParam: pageRequest }) => {
      return handleAsyncSafely(() => queryFn(getParams(pageRequest)))
    },
    initialPageParam: { pageParam: 0, pageSize: options?.pageSize ?? 20 },
    getNextPageParam: (response) => {
      return response.hasNextPage && response.pageNumber !== undefined
        ? {
            pageParam: response.pageNumber + 1,
            pageSize: options?.pageSize ?? 20,
          }
        : undefined
    },
    ...options,
  })
}
